import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { toast } from 'components/ui/toast-store'

import { SLIDE_CAP } from '../constants'
import { presentationEditPath } from '../featureFlag'
import * as presentationService from '../services/presentationService'
import type { EntityRef, PresentationScope, PresentationTemplate } from '../types/presentation.types'
import { DeckTargetField, type DeckTarget } from './DeckTargetField'

/**
 * Build stories from Explorer rows (MYD-506): one story per selected asset,
 * from a template, into a new deck or one of the user's own, then open it.
 *
 * Evaluation sites stand for their catalogue location; a site with none has
 * nothing to make a story about, and is counted rather than sent.
 *
 * The server takes GENERATE_BATCH assets per request, so a larger selection
 * is sent in batches with progress. The deck's slide cap is checked before
 * anything is sent: a batch that failed half way would leave a deck that is
 * neither what the user had nor what they asked for.
 */

export type StoryGrain = 'location' | 'project' | 'evaluationSite'

/** The asset type a grain's rows make stories about. */
export const storyScopeFor = (grain: StoryGrain): PresentationScope => (grain === 'project' ? 'project' : 'location')

interface BuildStoriesDialogProps {
	open: boolean
	onClose: () => void
	grain: StoryGrain
	/** The selected row ids: locations, projects, or evaluation sites. */
	ids: string[]
}

export function BuildStoriesDialog({ open, onClose, grain, ids }: BuildStoriesDialogProps) {
	const navigate = useNavigate()
	const scope = storyScopeFor(grain)
	const [templates, setTemplates] = useState<PresentationTemplate[] | null>(null)
	const [templateKey, setTemplateKey] = useState('')
	const [sources, setSources] = useState<EntityRef[] | null>(null)
	const [withoutLocation, setWithoutLocation] = useState(0)
	const [target, setTarget] = useState<DeckTarget | null>(null)
	const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [partialDeck, setPartialDeck] = useState<string | null>(null)

	// What the rows stand for, and the templates for that kind of asset.
	useEffect(() => {
		if (!open) return
		setError(null)
		setProgress(null)
		setPartialDeck(null)
		setSources(null)
		setWithoutLocation(0)
		let cancelled = false
		presentationService
			.listTemplates(scope)
			.then(list => {
				if (cancelled) return
				setTemplates(list)
				setTemplateKey(current => (list.some(template => template.key === current) ? current : list[0]?.key ?? ''))
			})
			.catch(() => !cancelled && setTemplates([]))
		if (grain === 'evaluationSite') {
			presentationService
				.locationsForSites(ids)
				.then(found => {
					if (cancelled) return
					setSources(found.locationIds.map(entityId => ({ scope: 'location', entityId })))
					setWithoutLocation(found.withoutLocation)
				})
				.catch(() => !cancelled && setError('The sites could not be looked up.'))
		} else {
			setSources(ids.map(entityId => ({ scope, entityId })))
		}
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const template = templates?.find(candidate => candidate.key === templateKey) ?? null
	const count = sources?.length ?? 0
	const existing = target?.kind === 'existing' ? target.slideCount : 0
	// An upper bound: slides the user may not see are left out on the way.
	const slides = existing + count * (template?.slideCount ?? 0)
	const overCap = slides > SLIDE_CAP
	const noun = scope === 'project' ? 'project' : 'location'
	const defaultName = useMemo(() => `${ids.length} ${ids.length === 1 ? noun : `${noun}s`}`, [ids.length, noun])

	const build = async () => {
		if (!template || !target || !sources || count === 0) return
		setError(null)
		let deckId = target.kind === 'existing' ? target.id : null
		let added = 0
		let skipped = 0
		let dropped = 0
		try {
			if (!deckId) deckId = (await presentationService.createPresentation({ name: target.kind === 'new' ? target.name : '' })).id
			for (let start = 0; start < count; start += presentationService.GENERATE_BATCH) {
				setProgress({ done: start, total: count })
				const batch = sources.slice(start, start + presentationService.GENERATE_BATCH)
				const result = await presentationService.generateStoriesInto(deckId, template.key, batch)
				added += result.added
				skipped += result.skipped.length
				dropped += result.dropped
			}
			setProgress({ done: count, total: count })
			const notes = [skipped > 0 && `${skipped} you cannot open were skipped`, dropped > 0 && `${dropped} elements you do not have access to were left out`].filter(Boolean)
			toast.success(`Added ${added} ${added === 1 ? 'story' : 'stories'}${notes.length ? `. ${notes.join('; ')}.` : '.'}`)
			navigate(presentationEditPath(deckId))
		} catch (cause: any) {
			setProgress(null)
			setError(cause?.response?.data?.message ?? 'The stories could not be added.')
			// Batches already sent are saved: offer the deck rather than hide it.
			if (deckId && added > 0) setPartialDeck(deckId)
		}
	}

	const busy = progress !== null && progress.done < progress.total

	return (
		<Dialog open={open} onClose={busy ? () => undefined : onClose}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>Build stories</DialogTitle>
					<DialogDescription>One story for each selected {noun}, from a template.</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					<div className="space-y-1.5">
						<Label htmlFor="build-stories-template">Template</Label>
						<Select id="build-stories-template" value={templateKey} onChange={event => setTemplateKey(event.target.value)}>
							{templates === null && <option value="">Loading...</option>}
							{(templates ?? []).map(candidate => (
								<option key={candidate.key} value={candidate.key}>
									{candidate.name} ({candidate.slideCount} {candidate.slideCount === 1 ? 'slide' : 'slides'})
								</option>
							))}
						</Select>
					</div>

					<DeckTargetField open={open} defaultName={defaultName} onChange={setTarget} />

					{sources !== null && (
						<p className={overCap ? 'text-xs font-medium text-destructive' : 'text-xs text-muted-foreground'} role={overCap ? 'alert' : undefined}>
							{count} {count === 1 ? 'story' : 'stories'} · up to {slides} slides
							{existing > 0 && ` with the ${existing} already in it`}
							{overCap && `. A presentation holds at most ${SLIDE_CAP}: select fewer rows or choose a shorter template.`}
						</p>
					)}
					{withoutLocation > 0 && (
						<p className="text-xs text-amber-700">
							{withoutLocation} {withoutLocation === 1 ? 'site is' : 'sites are'} not in your Locations catalogue, so {withoutLocation === 1 ? 'it has' : 'they have'} no story.
						</p>
					)}
					{progress && (
						<p className="text-xs text-gray-600" role="status">
							Adding stories… {Math.min(progress.done + presentationService.GENERATE_BATCH, progress.total)} of {progress.total}
						</p>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					{partialDeck && (
						<Button variant="ghost" isOutline onClick={() => navigate(presentationEditPath(partialDeck))}>
							Open the presentation
						</Button>
					)}
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={build} disabled={busy || !template || !target || count === 0 || overCap}>
						{busy ? 'Building...' : `Build ${count} ${count === 1 ? 'story' : 'stories'}`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default BuildStoriesDialog
